import { allTrackers } from "app/services/track/track";
import type { track as TrackType } from "pro-features/components/WithTrack/src/context";

export type SearchItem = "website" | "keyword";
export type CreatePlacement = "cta" | "plus icon" | "projects list";
export type ActionType = "view project" | "create a new project" | "create topics" | "create rank tracker";

export type HomePageWithDataTrackingEntity = {
    trackManagePlan: () => void;
    trackInviteTeammates: () => void;
    trackInviteTeammatesHover: () => void;
    trackSearchBarOpen: (withAI: boolean) => void;
    trackSearchBarClose: () => void;
    trackClickSearchBarInput: () => void;
    trackSearchBarTab: (tab: string) => void;
    trackSearchBarResult: (item: SearchItem, value: string, state?: string) => void;
    trackSearchBarReport: (reportName: string, state: string) => void;
    trackSearchBarUserQuery: (query: string, tab: string) => void;
    trackSearchBarAIAssistantClick: (query: string) => void;
    trackViewProject: () => void;
    trackViewAllProjects: () => void;
    trackClickCreateProject: (key: CreatePlacement) => void;
    trackSwitchProject: () => void;
    trackClickTrafficShare: (action: ActionType) => void;
    trackClickCompetitorsTrafficShare: (action: ActionType) => void;
    trackClickRankTracker: (action: ActionType) => void;
    trackClickRecent: (url: string) => void;
    trackViewAlert: () => void;
    trackViewAllAlerts: () => void;
    trackCreateProjectFromAlerts: () => void;
};

const createHomeWithDataTracking = (track: TrackType): HomePageWithDataTrackingEntity => ({
    trackManagePlan() {
        track("Button", "click", "manage plan");
    },
    trackInviteTeammates() {
        track("Button", "click", "invite teammates");
    },
    trackInviteTeammatesHover() {
        track("Button", "hover", "invite teammates");
    },
    trackSearchBarOpen(withAI) {
        track("Search Bar", "open", `homepage search/${withAI ? "with AI assistant" : "without AI assistant"}`);
    },
    trackSearchBarClose() {
        track("Search Bar", "close", "homepage search");
    },
    trackClickSearchBarInput() {
        track("Search Bar", "click", "search bar interaction");
    },
    trackSearchBarTab(tab) {
        track("Search Bar", "click", `search bar interaction/${tab}`);
    },
    trackSearchBarResult(key, value, state) {
        const statePrefix = state ? `${state}/` : "";
        track("Search Bar", "click", `search_result/${statePrefix}${key.toUpperCase()}/${value}`);
    },
    trackSearchBarReport(reportName, state) {
        track("Search Bar", "click", `search_result/${state}/REPORT/${reportName}`);
    },
    trackSearchBarUserQuery(query, tab) {
        track("Search Bar", "click", `search_result/${tab}/${query}`);
    },
    trackSearchBarAIAssistantClick(query) {
        track("Search Bar", "click", `homepage search/AI Assistant query/${query}`);
    },
    trackViewProject() {
        track("Button", "click", "view project/projects bar");
    },
    trackViewAllProjects() {
        track("Button", "click", "view all projects/projects bar");
    },
    trackClickCreateProject(key) {
        track("Button", "click", `create a new project/projects bar/${key}`);
    },
    trackSwitchProject() {
        track("Button", "click", "navigate to other project/projects bar");
    },
    trackClickRecent(url) {
        track("Button", "click", `view recent activities/${url}`);
    },
    trackClickTrafficShare(action: ActionType) {
        track("Button", "click", `${action}/traffic share`);
    },
    trackClickCompetitorsTrafficShare(action: ActionType) {
        track("Button", "click", `${action}/share of voice`);
    },
    trackViewAlert() {
        track("Button", "click", "view alert/priority alerts");
    },
    trackViewAllAlerts() {
        track("Button", "click", "view all alerts/priority alerts");
    },
    trackCreateProjectFromAlerts() {
        track("Button", "click", "create a new project/priority alerts");
    },
    trackClickRankTracker(action: ActionType) {
        track("Button", "click", `${action}/rank tracker`);
    },
});

export const homePageWithData = createHomeWithDataTracking(allTrackers.dispatchTrackEvent.bind(allTrackers));
